<template>
  <div class="flex flex-col gap-2 text-sm infuse-evse-details">
    <div class="group flex items-center gap-2 text-sm uppercase">
      {{ t('evse.title', { evseId: evse.ocppEvseId }) }}

      <InfuseTag :inline="true" variant="secondary" class="infuse-evse-status">
        {{ evse.evseStatus }}
      </InfuseTag>

      <InfuseTooltip
        :content="t('evse.capabilities.description')"
        :custom-content="true"
        :icon="['fal', 'list-check']"
        :placement="'right'"
        :title="
          t(
            `evse.capabilities.title.${
              evse.capabilities.length === 0 ? 'no' : evse.capabilities.length === 1 ? 'one' : 'many'
            }`,
            { amount: evse.capabilities.length }
          )
        "
        class="col-span-2 mb-2 infuse-evse-capabilities"
        offset-distance="8"
      >
        <InfuseTag variant="neutral" class="cursor-help">
          {{
            t(
              `evse.capabilities.title.${
                evse.capabilities.length === 0 ? 'no' : evse.capabilities.length === 1 ? 'one' : 'many'
              }`,
              { amount: evse.capabilities.length }
            )
          }}
        </InfuseTag>

        <template #content="{ content }">
          <div class="flex flex-col gap-2">
            <div class="mb-1 flex flex-wrap normal-case">
              {{ content }}
            </div>

            <CapabilitySelector :evse-id="evse.id" :read-only="true" :selected-capabilities="evse.capabilities" />
          </div>
        </template>
      </InfuseTooltip>

      <InfuseTooltip
        :content="t('evse.parkingRestrictions.description')"
        :custom-content="true"
        :icon="['fal', 'list-check']"
        :placement="'right'"
        :title="
          t(
            `evse.parkingRestrictions.title.${
              evse.parkingRestrictions.length === 0 ? 'no' : evse.parkingRestrictions.length === 1 ? 'one' : 'many'
            }`,
            { amount: evse.parkingRestrictions.length }
          )
        "
        class="col-span-2 mb-2 infuse-evse-parking-restrictions"
        offset-distance="8"
      >
        <InfuseTag variant="neutral" class="cursor-help">
          {{
            t(
              `evse.parkingRestrictions.title.${
                evse.parkingRestrictions.length === 0 ? 'no' : evse.parkingRestrictions.length === 1 ? 'one' : 'many'
              }`,
              { amount: evse.parkingRestrictions.length }
            )
          }}
        </InfuseTag>

        <template #content="{ content }">
          <div class="flex flex-col gap-2">
            <div class="mb-1 flex flex-wrap normal-case">
              {{ content }}
            </div>

            <ParkingRestrictionSelector
              :evse-id="evse.id"
              :read-only="true"
              :selected-parking-restrictions="evse.parkingRestrictions"
            />
          </div>
        </template>
      </InfuseTooltip>

      <div class="ml-4">
        <InfuseTooltip
          :content="t('action.editEvse.description')"
          :icon="['far', 'pen-to-square']"
          :title="t('action.editEvse.title')"
          class="col-span-2 mb-2 justify-end normal-case infuse-evse-edit"
          offset-distance="6"
        >
          <font-awesome-icon
            v-permission="{ id: commandPermissions.UPDATE_EVSE_STATICS }"
            :icon="['far', 'pen-to-square']"
            size="sm"
            class="cursor-pointer px-2 text-neutral-400 opacity-0 hover:text-neutral-500 group-hover:opacity-100"
            @click="editEvseRoamingSettingsModalVisible = true"
          />
        </InfuseTooltip>
      </div>
    </div>
    <div class="group flex items-center text-sm">
      <InfuseTag :inline="true" variant="neutral" class="font-medium">
        <font-awesome-icon :icon="['far', 'hashtag']" class="mr-2" />
        {{ evse.ocppEvseId }}
      </InfuseTag>

      <InfuseTag v-if="evse.floorLevel" :inline="true" variant="neutral" class="ml-4 font-medium">
        <font-awesome-icon :icon="['fal', 'stairs']" class="mr-2" />
        {{ evse.floorLevel }}
      </InfuseTag>

      <InfuseTag v-if="evse.physicalReference" :inline="true" variant="neutral" class="ml-4 font-medium">
        <font-awesome-icon :icon="['fal', 'eye']" class="mr-2" />
        {{ evse.physicalReference }}
      </InfuseTag>
    </div>

    <div class="flex flex-col gap-2 rounded-lg bg-neutral-50 p-3 infuse-evse-connector-details">
      <div
        v-for="connector in evse.connectors"
        :key="connector.id"
        :class="[
          'group flex w-full flex-col rounded-lg border-l-4 bg-white p-2 text-xs',
          EViolinBorderColorClasses[connectorStatusForConnector(connector)]
        ]"
      >
        <ConnectorDetails
          :charging-station="chargingStation"
          :connector="connector"
          :evse="evse"
          @reload-model="emit('reload-model')"
        />
      </div>
    </div>
  </div>

  <EditEvseRoamingDetailsModal
    v-if="editEvseRoamingSettingsModalVisible"
    :evse="evse"
    @close="editEvseRoamingSettingsModalVisible = false"
    @save="(updatedEvseRoamingSettings: EvseRoamingSettings) => updateEvseRoamingSettings(updatedEvseRoamingSettings)"
  />
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { commandPermissions, updateEvseStatics } from '@infuse/api/cpms/operator';
import { ChargingStation, connectorStatusForConnector, Evse } from '@infuse/cpms/charging-station/store';
import { CapabilitySelector } from '@infuse/cpms/config/ui';
import { useNotify } from '@infuse/cpms/ui';
import { InfuseTag, InfuseTooltip } from '@infuse/ui/components';
import axios from 'axios';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ConnectorDetails from './ConnectorDetails.vue';
import EditEvseRoamingDetailsModal from './EditEvseRoamingDetailsModal.vue';
import { EvseRoamingSettings } from './types/RoamingSettings';
import ParkingRestrictionSelector from './ParkingRestrictionSelector.vue';
import { vPermission } from '@infuse/iam/permission/directives';
import { EViolinBorderColorClasses } from '@infuse/ui/utils';

const { notifySuccess, notifyFailure } = useNotify();
const { t } = useI18n();

interface Props {
  chargingStation: ChargingStation;
  evse: Evse;
}
const props = defineProps<Props>();

interface Emits {
  (e: 'reload-model'): void;
}
const emit = defineEmits<Emits>();

const editEvseRoamingSettingsModalVisible = ref(false);

const updateEvseRoamingSettings = async (updatedEvseRoamingSettings: EvseRoamingSettings) => {
  if (!updatedEvseRoamingSettings) {
    return;
  }

  try {
    await updateEvseStatics(
      { chargingStationId: props.chargingStation.id },
      {
        evseRoamingData: [
          {
            evseId: updatedEvseRoamingSettings.evseId,
            capabilities: updatedEvseRoamingSettings.capabilities,
            floorLevel: updatedEvseRoamingSettings.floorLevel,
            physicalReference: updatedEvseRoamingSettings.physicalReference,
            parkingRestrictions: updatedEvseRoamingSettings.parkingRestrictions
          }
        ]
      }
    );

    notifySuccess({
      title: t('notification.success.update.title'),
      message: t('notification.success.update.message')
    });

    editEvseRoamingSettingsModalVisible.value = false;
    emit('reload-model');
  } catch (error) {
    notifyFailure({
      title: t('notification.error.update.title'),
      message: t('notification.error.update.message', {
        reason: axios.isAxiosError(error) ? ` '${error.response?.data?.message}'` : ''
      })
    });
  }
};
</script>

<i18n>
{
  "en": {
    "action": {
      "editEvse": {
        "title": "Edit roaming settings",
        "description": "The configured roaming settings for this EVSE. These settings do not influence the ability to manage the charging station / EVSE, and are only used to improve the charging experience of EV drivers."
      }
    },
    "evse": {
      "title": "Evse {evseId}",
      "capabilities": {
        "title": {
          "no": "No capabilities",
          "one": "1 capability",
          "many": "{amount} capabilities"
        },
        "description": "The configured roaming capabilities for this EVSE. These capabilities do not influence the ability to manage the charging station, and are only used to improve the charging experience of EV drivers."
      },
      "parkingRestrictions": {
        "title": {
          "no": "No parking restrictions",
          "one": "1 parking restriction",
          "many": "{amount} parking restrictions"
        },
        "description": "The configured parking restrictions for this EVSE. These parking restrictions do not influence the ability to manage the charging station, and are only used to improve the charging experience of EV drivers."
      }
    },
    "notification": {
      "success": {
        "update": {
          "title": "Update successful",
          "message": "EVSE's roaming settings have been updated."
        }
      },
      "error": {
        "update": {
          "title": "Update failed",
          "message": "Failed to update EVSE's roaming settings. {reason}"
        }
      }
    }
  },
  "nl": {
    "action": {
      "editEvse": {
        "title": "Roaming-instellingen aanpassen",
        "description": "De ingestelde roaming-instellingen voor deze EVSE. Deze instellingen hebben geen invloed op het kunnen beheren van de laadpaal, en worden enkel gebruikt om de laad-ervaring van EV-bereiders te verbeteren."
      }
    },
    "evse": {
      "title": "Evse {evseId}",
      "capabilities": {
        "title": {
          "no": "Geen functies",
          "one": "1 functie",
          "many": "{amount} functies"
        },
        "description": "De ingestelde roaming-functies voor deze EVSE. Deze functies hebben geen invloed op het kunnen beheren van de laadpaal, en worden enkel gebruikt om de laad-ervaring van EV-bereiders te verbeteren."
      },
      "parkingRestrictions": {
        "title": {
          "no": "Geen parkeerbeperkingen",
          "one": "1 parkeerbeperking",
          "many": "{amount} parkeerbeperkingen"
        },
        "description": "De ingestelde parkeerbeperkingen voor deze EVSE. Deze parkeerbeperkingen hebben geen invloed op het kunnen beheren van de laadpaal, en worden enkel gebruikt om de laad-ervaring van EV-bereiders te verbeteren."
      }
    },
    "notification": {
      "success": {
        "update": {
          "title": "Aanpassen gelukt",
          "message": "EVSE's roaming-instellingen zijn aangepast."
        }
      },
      "error": {
        "update": {
          "title": "Aanpassen mislukt",
          "message": "EVSE's roaming-instellingen konden niet worden aangepast. {reason}"
        }
      }
    }
  }
}
</i18n>
